import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Tag, Search } from 'lucide-react';

export default function ItemsPage() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', gold_purity: '', min_quantity: 0, supplier_name: '' });
  const [editForm, setEditForm] = useState({ name: '', gold_purity: '', min_quantity: 0, supplier_name: '' });

  const fetchItems = async () => {
    try {
      const res = await API.get('/items');
      setItems(res.data);
    } catch(e) { console.error(e); }
  };

  useEffect(() => { fetchItems(); }, []);

  const filteredItems = items.filter(item =>
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.supplier_name?.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Item name is required'); return; }
    // Check for duplicate names
    if (items.some(i => i.name.toLowerCase() === form.name.trim().toLowerCase())) {
      toast.error('An item with this name already exists');
      return;
    }
    setLoading(true);
    try {
      await API.post('/items', {
        name: form.name.trim(),
        gold_purity: form.gold_purity,
        min_quantity: parseInt(form.min_quantity) || 0,
        supplier_name: form.supplier_name.trim(),
        box_ids: [],
      });
      toast.success(`Item "${form.name}" created`);
      setShowCreate(false);
      setForm({ name: '', gold_purity: '', min_quantity: 0, supplier_name: '' });
      fetchItems();
    } catch(err) {
      toast.error(err.response?.data?.detail || 'Failed to create item');
    } finally { setLoading(false); }
  };

  const handleEdit = async () => {
    if (!editItem) return;
    if (!editForm.name.trim()) { toast.error('Item name is required'); return; }
    try {
      await API.put(`/items/${editItem.id}`, {
        name: editForm.name.trim(),
        gold_purity: editForm.gold_purity,
        min_quantity: parseInt(editForm.min_quantity) || 0,
        supplier_name: editForm.supplier_name.trim(),
        box_ids: editItem.box_ids || [],
      });
      toast.success('Item updated');
      setEditItem(null);
      fetchItems();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete item "${item.name}"? This cannot be undone.`)) return;
    try {
      await API.delete(`/items/${item.id}`);
      toast.success(`Item "${item.name}" deleted`);
      fetchItems();
    } catch(err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.name || '',
      gold_purity: item.gold_purity || '',
      min_quantity: item.min_quantity || 0,
      supplier_name: item.supplier_name || '',
    });
  };

  return (
    <div data-testid="items-page">
      <div className="page-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h1>Item Names</h1>
          <p className="hidden sm:block">Manage item names that can be assigned to boxes (e.g., Ring, Chain, Bangle)</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto" data-testid="item-create-button">
          <Plus size={16} className="mr-2" />Add Item
        </Button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} data-testid="items-search-input" />
        </div>
        <span className="text-sm text-muted-foreground">{items.length} item{items.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Empty State */}
      {items.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Tag size={48} className="mx-auto mb-4 text-muted-foreground/40" />
            <h3 className="text-lg font-semibold mb-2">No Items Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add item names like "Ring", "Chain", "Bangle", "Necklace" etc.<br />
              These will appear as options when creating or editing boxes.
            </p>
            <Button onClick={() => setShowCreate(true)} data-testid="item-create-empty-button">
              <Plus size={16} className="mr-2" />Add Your First Item
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Mobile Card View */}
      {filteredItems.length > 0 && (
        <div className="mobile-card-list">
          {filteredItems.map(item => (
            <Card key={item.id}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{item.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {item.gold_purity && <span className={`status-badge purity-${item.gold_purity} text-xs`}>{item.gold_purity}</span>}
                      {item.supplier_name && <span className="text-xs text-muted-foreground">{item.supplier_name}</span>}
                    </div>
                    {item.min_quantity > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">Min qty: {item.min_quantity}</p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Desktop Table View */}
      {filteredItems.length > 0 && (
        <div className="desktop-table">
          <Card>
            <CardContent className="p-0">
              <div className="table-scroll-wrapper">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Purity</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Min Quantity</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map(item => (
                      <TableRow key={item.id}>
                        <TableCell className="font-semibold">{item.name}</TableCell>
                        <TableCell>
                          {item.gold_purity ? <span className={`status-badge purity-${item.gold_purity}`}>{item.gold_purity}</span> : <span className="text-muted-foreground">Any</span>}
                        </TableCell>
                        <TableCell>{item.supplier_name || <span className="text-muted-foreground">&mdash;</span>}</TableCell>
                        <TableCell className="text-right font-mono">{item.min_quantity || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(item)} data-testid={`item-edit-${item.id}`}><Pencil size={14} /></Button>
                            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item)} data-testid={`item-delete-${item.id}`}><Trash2 size={14} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Item Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Item</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Item Name *</label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Ring, Chain, Bangle, Necklace"
                required
                data-testid="item-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Associated Purity</label>
              <Select value={form.gold_purity || 'any'} onValueChange={v => setForm({ ...form, gold_purity: v === 'any' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Any purity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="76">76</SelectItem>
                  <SelectItem value="84">84</SelectItem>
                  <SelectItem value="92">92</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Optional: restrict this item to a specific purity</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Supplier Name</label>
              <Input
                value={form.supplier_name}
                onChange={e => setForm({ ...form, supplier_name: e.target.value })}
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Min Quantity (for PO alerts)</label>
              <Input
                type="text"
                inputMode="numeric"
                value={form.min_quantity}
                onChange={e => setForm({ ...form, min_quantity: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground mt-1">Auto-generate purchase order when stock falls below this</p>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="item-create-submit">
              {loading ? 'Creating...' : 'Add Item'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Item: {editItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Item Name *</label>
              <Input
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="item-edit-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Associated Purity</label>
              <Select value={editForm.gold_purity || 'any'} onValueChange={v => setEditForm({ ...editForm, gold_purity: v === 'any' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Any purity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="76">76</SelectItem>
                  <SelectItem value="84">84</SelectItem>
                  <SelectItem value="92">92</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Supplier Name</label>
              <Input
                value={editForm.supplier_name}
                onChange={e => setEditForm({ ...editForm, supplier_name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Min Quantity</label>
              <Input
                type="text"
                inputMode="numeric"
                value={editForm.min_quantity}
                onChange={e => setEditForm({ ...editForm, min_quantity: e.target.value.replace(/[^0-9]/g, '') })}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEdit} className="flex-1" data-testid="item-edit-save">Save Changes</Button>
              <Button variant="destructive" onClick={() => { handleDelete(editItem); setEditItem(null); }}>Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
